/**
 * NotificationBell.js
 * Admin notifications bell dropdown with real-time SignalR updates
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { NavDropdown, Badge, Spinner, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import 'moment/locale/it';
import { notificationService } from '../../services/api';

moment.locale('it');

const NotificationBell = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const hubConnectionRef = useRef(null);

  const loadNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const res = await notificationService.getNotifications();
      if (res && res.success && Array.isArray(res.data)) {
        setNotifications(res.data);
      }
    } catch (err) {
      console.error('Error fetching admin notifications:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadNotifications();

    // Start SignalR real-time connection
    const hub = notificationService.startSignalRConnection((newNotification) => {
      setNotifications((prev) => [
        newNotification,
        ...prev.filter((n) => n.id !== newNotification.id),
      ]);
    });
    hubConnectionRef.current = hub;

    return () => {
      if (hubConnectionRef.current && typeof hubConnectionRef.current.stop === 'function') {
        hubConnectionRef.current.stop();
      }
    };
  }, [loadNotifications]);

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    try {
      setNotifications((prev) => prev.filter((n) => n.id !== id));
      await notificationService.deleteNotification(id);
    } catch (err) {
      console.error('Error deleting notification:', err);
      loadNotifications();
    }
  };

  const handleDeleteAll = async (e) => {
    e.stopPropagation();
    try {
      setNotifications([]);
      await notificationService.deleteAllNotifications();
    } catch (err) {
      console.error('Error deleting all notifications:', err);
      loadNotifications();
    }
  };

  const handleNotificationClick = (notification) => {
    if (notification.entityType === 'User' && notification.entityId) {
      navigate(`/users/${notification.entityId}/edit`);
      setIsOpen(false);
    }
  };

  const count = notifications.length;

  return (
    <NavDropdown
      show={isOpen}
      onToggle={(nextShow) => setIsOpen(nextShow)}
      align="end"
      id="notification-dropdown"
      className="notification-dropdown me-2"
      title={
        <span
          className="position-relative d-inline-flex align-items-center text-white p-1"
          title="Notifiche"
          data-testid="notification-bell-trigger"
        >
          <i className="fas fa-bell fs-5"></i>
          {count > 0 && (
            <Badge
              bg="danger"
              pill
              className="position-absolute top-0 start-100 translate-middle"
              style={{ fontSize: '0.65rem' }}
              data-testid="notification-badge"
            >
              {count > 99 ? '99+' : count}
            </Badge>
          )}
        </span>
      }
    >
      <div style={{ width: '340px', maxWidth: '90vw' }}>
        <div className="d-flex justify-content-between align-items-center px-3 py-2 border-bottom bg-light">
          <div className="fw-bold d-flex align-items-center gap-1">
            <i className="fas fa-bell me-1 text-primary"></i>
            <span>Notifiche</span>
            {count > 0 && (
              <Badge bg="primary" pill data-testid="notification-header-count">
                {count}
              </Badge>
            )}
          </div>
          {count > 0 && (
            <Button
              variant="link"
              size="sm"
              className="p-0 text-danger text-decoration-none"
              onClick={handleDeleteAll}
              title="Cancella tutte le notifiche"
              data-testid="delete-all-notifications-btn"
            >
              <i className="fas fa-trash-alt me-1"></i>
              Cancella tutte
            </Button>
          )}
        </div>

        <div style={{ maxHeight: '360px', overflowY: 'auto' }}>
          {loading && count === 0 ? (
            <div className="text-center py-4 text-muted">
              <Spinner animation="border" size="sm" className="me-2" />
              Caricamento...
            </div>
          ) : count === 0 ? (
            <div className="text-center py-4 text-muted px-3" data-testid="no-notifications-msg">
              <i className="far fa-bell-slash fs-3 d-block mb-2 text-secondary"></i>
              <div>Nessuna nuova notifica</div>
            </div>
          ) : (
            notifications.map((item) => (
              <div
                key={item.id}
                data-testid={`notification-item-${item.id}`}
                className="notification-item px-3 py-2 border-bottom d-flex align-items-start gap-2 text-wrap"
                style={{
                  cursor: item.entityType === 'User' && item.entityId ? 'pointer' : 'default',
                  backgroundColor: '#ffffff',
                  transition: 'background-color 0.15s ease-in-out',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f8f9fa';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#ffffff';
                }}
                onClick={() => handleNotificationClick(item)}
              >
                <div className="mt-1 text-primary">
                  <i className="fas fa-user-edit"></i>
                </div>
                <div className="flex-grow-1" style={{ minWidth: 0 }}>
                  <div className="fw-semibold small text-dark">{item.title}</div>
                  <div className="small text-muted mb-1" style={{ lineHeight: '1.25' }}>
                    {item.message}
                  </div>
                  <div
                    className="text-muted"
                    style={{ fontSize: '0.75rem' }}
                    title={moment(item.createdAt).format('DD/MM/YYYY HH:mm')}
                  >
                    <i className="far fa-clock me-1"></i>
                    {moment(item.createdAt).fromNow()}
                  </div>
                </div>
                <button
                  type="button"
                  className="btn btn-link btn-sm p-1 text-muted"
                  title="Cancella notifica"
                  data-testid={`delete-notification-btn-${item.id}`}
                  onClick={(e) => handleDelete(e, item.id)}
                  style={{ textDecoration: 'none' }}
                >
                  <i className="fas fa-trash-alt text-danger"></i>
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </NavDropdown>
  );
};

export default NotificationBell;
